import { getParameterFromURL } from './getParameterFromUrl';

// Cross-funnel user id: stitches Amplitude events together across the onboarding,
// the email (AWS SES) and this payment page. The payment page is not an entry
// point of the funnel, so the id is only *accepted* here (?fuid → localStorage →
// the id Amplitude already knows) and never generated: a visitor with none of the
// three sources reached the paywall bypassing the onboarding, and inventing an id
// would hide that broken entry instead of leaving it visible.
const STORAGE_KEY = 'frontend_user_id';
const URL_PARAM = 'fuid';

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch (error) {
    return null;
  }
}

function persist(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch (error) {
    // Private mode or blocked storage: the id lives for this page load only.
  }
}

function readFromUrl(): string | null {
  try {
    return getParameterFromURL(URL_PARAM) || null;
  } catch (error) {
    // Malformed percent-encoding in the value — treat as absent.
    return null;
  }
}

function stripParamFromUrl(): void {
  try {
    const search = window.location.search;
    if (!search) return;

    // Cut out only the fuid pair instead of rebuilding the query string through
    // URLSearchParams: re-serialising would re-encode the other parameters (the
    // base64 of ?ev=, the onboarding answers), and those are read with a regex
    // over the raw href.
    const currentQuery = search.slice(1);
    const nextQuery = currentQuery
      .split('&')
      .filter(pair => pair !== URL_PARAM && !pair.startsWith(`${URL_PARAM}=`))
      .join('&');

    if (nextQuery === currentQuery) return;

    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`
    );
  } catch (error) {
    // A hardened WebView can reject replaceState — keeping the param in the
    // address bar is harmless, the id is already resolved.
  }
}

function readFromAmplitude(): string | null {
  try {
    return window.amplitude?.getUserId() || null;
  } catch (error) {
    return null;
  }
}

/**
 * Must be called before `amplitude.init` — the result is passed in as `userId`
 * so that the very first events (session_start / attribution / page_view) already
 * carry it. Returns `undefined` when there is nothing to apply: Amplitude then
 * keeps whatever user_id it has stored, or stays anonymous on its device_id.
 */
export function resolveFrontendUserId(): string | undefined {
  const fromUrl = readFromUrl();

  // Arrival from the onboarding or from an email link: the URL value always wins
  // over the local one, and the param itself is dropped from the address bar
  // without a reload.
  if (fromUrl) {
    persist(fromUrl);
    stripParamFromUrl();

    return fromUrl;
  }

  // A previous visit to this domain.
  return readStored() || undefined;
}

export default function getFrontendUserId(): string | null {
  return readStored() || readFromAmplitude();
}
